import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import StyledTable, { TableBody, TableHead, TableCell, TableRow } from 'components/global/Table';
import { Drawer, Select, MenuItem, Grid } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import NavigationClose from '@mui/icons-material/Close';

import GlobalTitle from '../global/Title';
import ClientSelect from '../global/ClientSelect';
import ConfirmationDialog from '../global/ConfirmationDialog';
import TopBar from './partials/topBar';
import GlobalPanel from '../global/Panel';
import DataLoadingSpinner from '../global/DataLoadingSpinner';
import SendFiles from './SendFiles';
import { fetchPaginatedSharedWith, removeFile } from '../../actions/resources';
import { getPreferenceTz } from 'utils/timezone';

const propTypes = {
  fetchPaginatedSharedWith: PropTypes.func.isRequired,
  removeFile: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

function FilesToClients({
  fetchPaginatedSharedWith: fetchPaginatedSharedWithAction,
  removeFile: removeFileAction,
  intl,
}) {
  const [openSendingFile, setOpenSendingFile] = useState(false);
  const [showSnack, setShowSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [files, setFiles] = useState([]);
  const [pagination, setPagination] = useState({});
  const [clientId, setClientId] = useState(null);
  const [sortBy, setSortBy] = useState('date-newest');
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchList = (page, overrides = {}) => {
    const effectiveClientId =
      overrides.clientId !== undefined ? overrides.clientId : clientId;
    const effectiveSortBy =
      overrides.sortBy !== undefined ? overrides.sortBy : sortBy;
    setLoading(true);
    fetchPaginatedSharedWithAction(
      page,
      effectiveClientId,
      effectiveSortBy,
      (result, error) => {
        setLoading(false);
        if (!error) {
          setFiles(result.data);
          setPagination(result.pagination_info);
        }
      },
    );
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSnackMessage = message => {
    setShowSnack(true);
    setSnackMessage(message);
  };

  const hideSnack = () => setShowSnack(false);

  const showSendingFile = () => setOpenSendingFile(true);

  const hideSendingFile = () => {
    setOpenSendingFile(false);
    fetchList();
  };

  const showDeleteConfirm = fileId => {
    setDeletingFileId(fileId);
    setOpenDeleteConfirm(true);
  };

  const hideDeleteConfirm = () => setOpenDeleteConfirm(false);

  const handleClientSelectChange = newClientId => {
    setClientId(newClientId);
    fetchList(1, { clientId: newClientId });
  };

  const handleSortChange = event => {
    const newSortBy = event.target.value;
    setSortBy(newSortBy);
    fetchList(1, { sortBy: newSortBy });
  };

  const deleteFile = id => {
    removeFileAction(id, () => {
      showSnackMessage(
        <FormattedMessage defaultMessage="Successfully deleted file." />,
      );
      fetchList(1);
    });
  };

  const renderRows = () => {
    const { formatDate } = intl;

    return files.map(file => (
      <TableRow key={file.id}>
        <TableCell>
          {formatDate(file.created_at, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: getPreferenceTz(),
          })}
        </TableCell>
        <TableCell className="process-name">
          {file.shared_user}
        </TableCell>
        <TableCell>
          {file.file_name.replace(/__([a-zA-Z0-9])+\./, '.')}
        </TableCell>
        <TableCell>
          <div style={{ display: 'flex' }}>
            <Button
              href={file.resource}
              className="flat-button-baby-blue process-action-button pab-small"
              download
              target="_blank"
            >{<FormattedMessage defaultMessage="View" />}</Button>
            &nbsp;
            <Button
              onClick={() => showDeleteConfirm(file.id)}
              className="flat-button-red process-action-button pab-small"
            >{<FormattedMessage defaultMessage="Delete" />}</Button>
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  const renderTable = () => (
    <>
      <StyledTable>
        <TableHead>
          <TableRow>
            <TableCell>
              <FormattedMessage defaultMessage="Date" />
            </TableCell>
            <TableCell>
              <FormattedMessage defaultMessage="Client" />
            </TableCell>
            <TableCell>
              <FormattedMessage defaultMessage="File" />
            </TableCell>
            <TableCell>
              <FormattedMessage defaultMessage="Action" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderRows()}</TableBody>
      </StyledTable>
      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: 30 }}
      >
        {pagination && pagination.total && pagination.total_items ? (
          <Pagination
            shape="rounded"
            count={pagination.total}
            page={pagination.current}
            onChange={(_, page) => fetchList(page)}
          />
        ) : (
          <noscript />
        )}
      </div>
    </>
  );

  const { formatMessage } = intl;
  return (
    <div>
      <TopBar activeTab="files_to_clients" />
      <Drawer
        anchor="right"
        variant="temporary"
        open={openSendingFile}
        onClose={hideSendingFile}
        slotProps={{ paper: { style: { width: '40%' } } }}
      >
        <div style={{ marginLeft: '40px', marginTop: '32px' }}>
          <GlobalTitle
            title={<FormattedMessage defaultMessage="Send Files" />}
          >
            <NavigationClose
              onClick={hideSendingFile}
              className="closeButtonTopRight"
              style={{ marginRight: '35px' }}
            />
          </GlobalTitle>
          {openSendingFile ? (
            <SendFiles
              closeModal={hideSendingFile}
              openSnack={showSnackMessage}
              toAll
            />
          ) : (
            <div />
          )}
        </div>
      </Drawer>
      <GlobalPanel>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: 30,
          }}
        >
          <Grid container spacing={2}>
            <Grid size="auto">
              <div style={{ width: 250 }}>
                <ClientSelect onChange={handleClientSelectChange} />
              </div>
            </Grid>
            <Grid size="auto">
              <Select
                value={sortBy}
                onChange={handleSortChange}
              >
                <MenuItem value="date-newest">
                  {formatMessage({
                    defaultMessage: 'Date - Newest First',
                  })}
                </MenuItem>
                <MenuItem value="date-oldest">
                  {formatMessage({
                    defaultMessage: 'Date - Oldest First',
                  })}
                </MenuItem>
              </Select>
            </Grid>
          </Grid>
          <Button
            className="flat-button-green"
            onClick={showSendingFile}
          >{<FormattedMessage defaultMessage="SEND NEW FILE" />}</Button>
        </div>
        {loading ? <DataLoadingSpinner /> : renderTable()}
      </GlobalPanel>
      {openDeleteConfirm && (
        <ConfirmationDialog
          open={openDeleteConfirm}
          message={formatMessage({
            defaultMessage: 'Are you sure you want to delete this file?',
          })}
          handleConfirm={() => deleteFile(deletingFileId)}
          handleClose={hideDeleteConfirm}
        />
      )}
      <Snackbar
        open={showSnack}
        message={snackMessage}
        autoHideDuration={4000}
        onClose={hideSnack}
      />
    </div>
  );
}

FilesToClients.propTypes = propTypes;

export default injectIntl(
  connect(null, { fetchPaginatedSharedWith, removeFile })(FilesToClients),
);
